import {
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import isNil from "lodash.isnil";
import {
  Activity,
  BattlesExistsApiResponse,
  BattlesExistsRequest,
  GetBattleByIdApiResponse,
  GetBattleHypeboardScoresResponse,
  GetBattlesApiResponse,
  GetBattlesRequest,
  GetBattleStatsResponse,
  GetCommentsApiResponse,
  GetRelatedBattlesApiResponse,
  HypeboardTimeframe,
  PaginatedResponse,
  PostBattleCommentRequest,
  StarBattleRequest,
} from "@dripit/types";

import { queryKeys } from "@/api/constants";
import { client } from "@/api/base";

export const useBattleById = (id: string, enabled = true) =>
  useQuery<GetBattleByIdApiResponse>({
    queryKey: [queryKeys.BATTLE, id],
    queryFn: () =>
      client.clearParams().get<GetBattleByIdApiResponse>(`battles/${id}`),
    staleTime: 10_000,
    enabled: !isNil(id) && enabled,
  });

export interface InfiniteBattlesQueryResponse {
  pages: GetBattlesApiResponse[];
  pageParams: number[];
}

export const useBattles = (
  params: Omit<GetBattlesRequest, "page" | "pageSize"> = {}
): UseInfiniteQueryResult<InfiniteBattlesQueryResponse> => {
  return useInfiniteQuery({
    queryKey: [queryKeys.BATTLES, params],
    queryFn: ({ pageParam }: { pageParam: number }) =>
      client
        .setConfig({
          params: {
            page: pageParam,
            pageSize: 12,
            ...params,
            hypeBoardsIds: params.hypeBoardsIds?.join(","),
          },
        })
        .get<GetBattlesApiResponse>("battles"),
    staleTime: 0,
    gcTime: 0,
    initialPageParam: 1,
    getNextPageParam: (response) => {
      const nextPage = response.current_page + 1;
      return nextPage > response.total_pages ? null : nextPage;
    },
  });
};

export const useRelatedBattles = (id?: number) => {
  return useQuery<GetRelatedBattlesApiResponse>({
    queryKey: [queryKeys.RELATED_BATTLES, id],
    queryFn: () =>
      client
        .clearParams()
        .get<GetRelatedBattlesApiResponse>(`battles/${id}/related`),
    staleTime: 0,
    enabled: !isNil(id),
  });
};

export const useBattleHypeboardScores = (
  id?: string,
  timeframe?: HypeboardTimeframe
) =>
  useQuery<GetBattleHypeboardScoresResponse>({
    queryKey: [queryKeys.BATTLE_HYPEBOARD_SCORES, id, timeframe],
    queryFn: () =>
      client
        .clearParams()
        .get<GetBattleHypeboardScoresResponse>(
          `battles/${id}/hypemeter-scores?timeFrame=${timeframe ?? HypeboardTimeframe["7D"]}`
        ),
    staleTime: 10_000,
    enabled: !isNil(id),
  });

export const useBattleStats = (id?: string) =>
  useQuery<GetBattleStatsResponse>({
    queryKey: [queryKeys.BATTLE_STATS, id],
    queryFn: () =>
      client
        .clearParams()
        .get<GetBattleStatsResponse>(`battles/${id}/battle-stats`),
    staleTime: 10_000,
    enabled: !isNil(id),
  });

interface MyBattlesProps {
  past?: boolean;
  enabled?: boolean;
  artistName?: string;
}

export const useMyBattles = ({
  past = false,
  enabled = true,
  artistName,
}: MyBattlesProps): UseInfiniteQueryResult<InfiniteBattlesQueryResponse> =>
  useInfiniteQuery({
    queryKey: [queryKeys.MY_BATTLES, past, artistName],
    queryFn: ({ pageParam }: { pageParam: number }) =>
      client
        .setConfig({
          params: { page: pageParam, pageSize: 10, past, artistName },
        })
        .get<GetBattlesApiResponse>("battles/user"),
    staleTime: 10_000,
    initialPageParam: 1,
    getNextPageParam: (response) => {
      const nextPage = response.current_page + 1;
      return nextPage > response.total_pages ? null : nextPage;
    },
    enabled,
  });

interface UserBattlesProps {
  walletAddress?: string;
  past?: boolean;
  artistName?: string;
}

export const useUserBattles = ({
  walletAddress,
  past = false,
  artistName,
}: UserBattlesProps): UseInfiniteQueryResult<InfiniteBattlesQueryResponse> =>
  useInfiniteQuery({
    queryKey: [queryKeys.USER_BATTLES, walletAddress, past, artistName],
    queryFn: ({ pageParam }: { pageParam: number }) =>
      client
        .setConfig({
          params: {
            page: pageParam,
            pageSize: 10,
            wallet: walletAddress,
            past,
            artistName,
          },
        })
        .get<GetBattlesApiResponse>("battles/user"),
    staleTime: 10_000,
    initialPageParam: 1,
    getNextPageParam: (response) => {
      const nextPage = response.current_page + 1;
      return nextPage > response.total_pages ? null : nextPage;
    },
    enabled: !isNil(walletAddress),
  });

export const postBattleComment = (id: string) =>
  useMutation({
    mutationFn: (comment: PostBattleCommentRequest) =>
      client
        .clearParams()
        .post<GetCommentsApiResponse>(`battles/${id}/comment`, comment),
  });

export const starBattle = (id: string) =>
  useMutation({
    mutationFn: (req: StarBattleRequest) =>
      client
        .clearParams()
        .post<string>(`battles/${id}/starred`, { payload: req }),
  });

export interface BattleActivitiesResponse {
  pages: PaginatedResponse<Activity>[];
  pageParams: number[];
}

interface UseBattleActivitiesProps {
  id?: number;
  pageSize?: number;
}

export const useBattleActivities = ({
  id,
  pageSize = 10,
}: UseBattleActivitiesProps): UseInfiniteQueryResult<BattleActivitiesResponse> =>
  useInfiniteQuery({
    queryKey: [queryKeys.BATTLE_ACTIVITIES, id],
    queryFn: ({ pageParam }: { pageParam: number }) =>
      client
        .setConfig({ params: { page: pageParam, pageSize } })
        .get<PaginatedResponse<Activity>>(`battles/${id}/activities`),
    staleTime: 10_000,
    initialPageParam: 1,
    getNextPageParam: (response) => {
      const nextPage = response.current_page + 1;
      return nextPage > response.total_pages ? null : nextPage;
    },
    enabled: !isNil(id),
  });

export const postBattleExists = () =>
  useMutation({
    mutationFn: (req: BattlesExistsRequest) =>
      client
        .clearParams()
        .post<BattlesExistsApiResponse>(`battles/battle-exists`, req),
  });
